import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../lib/store.ts';
import { type Profile } from '../lib/auth.ts';
import { axiosClient, signOut } from '../lib/api.ts';
import { StravaConnect } from '../components/StravaConnect.tsx';
import { GarminConnect } from '../components/GarminConnect.tsx';
import { IntervalsConnect } from '../components/IntervalsConnect.tsx';
import { useGarminEnabled, useIntervalsEnabled, useStravaEnabled } from '../hooks/usePlan.ts';
import { AccountTab } from '../components/profile/AccountTab.tsx';
import { CyclingTab } from '../components/profile/CyclingTab.tsx';
import { NutritionTab } from '../components/profile/NutritionTab.tsx';
import type { FtpPoint } from '../components/profile/FtpChart.tsx';
import { formatCsv } from '../components/profile/profileOptions.ts';
import { DeviceSetup } from '../components/setup/DeviceSetup.tsx';
import { ImportRidesCard } from '../components/ImportRidesCard.tsx';

type ProfileTab = 'account' | 'cycling' | 'nutrition' | 'integrations' | 'danger';

function tabActiveClass(tab: ProfileTab): string {
  if (tab === 'danger') return 'bg-red-500/20 text-red-400 border-red-500';
  if (tab === 'nutrition') return 'bg-emerald-500/20 text-emerald-400 border-emerald-500';
  return 'bg-blue-500/20 text-blue-400 border-blue-500';
}

function tabClass(currentTab: ProfileTab, tab: ProfileTab): string {
  if (currentTab === tab) return tabActiveClass(tab);
  return 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border) hover:border-gray-400';
}

const EMPTY_PROFILE: Profile = {
  ftp: null,
  weight_kg: null,
  height_cm: null,
  birth_year: null,
  lthr: null,
  max_hr: null,
  level: null,
  goals: null,
  sex: null,
  age: null,
  primary_sport: null,
  weekly_training_hours: null,
  goal: null,
  dietary_preferences: null,
  allergies: null,
  excluded_foods: null,
};

// Integrations is for everyone: the device picker and recommended ways to
// get rides in sit above whichever connect cards the user's flags allow.
const PROFILE_TABS: readonly ProfileTab[] = ['account', 'cycling', 'nutrition', 'integrations', 'danger'];

/** The tab named in ``?tab=`` (links such as "Connect"), else Cycling. */
function initialTab(param: string | null): ProfileTab {
  return PROFILE_TABS.find((tab) => tab === param) ?? 'cycling';
}

export function ProfilePage() {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const clearUser = useStore((s) => s.clearUser);
  const storeSetUser = useStore((s) => s.setUser);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<ProfileTab>(() => initialTab(searchParams.get('tab')));
  const stravaEnabled = useStravaEnabled();
  const garminEnabled = useGarminEnabled();
  const intervalsEnabled = useIntervalsEnabled();
  const [profile, setProfile] = useState<Profile>(user?.profile ?? EMPTY_PROFILE);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [ftpHistory, setFtpHistory] = useState<FtpPoint[]>([]);
  // Saving is blocked until GET /profile succeeds: otherwise a failed or
  // slow load leaves EMPTY_PROFILE in the form and Save PUTs nulls over
  // the real profile.
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Set once the user edits a field, so a late GET doesn't wipe the edits.
  const edited = useRef(false);
  const editProfile = useCallback((p: Profile) => {
    edited.current = true;
    setProfile(p);
  }, []);

  // Raw text for comma-separated inputs (allergies, excluded foods). The
  // parsed string[] lives on ``profile`` and is what the API sees. Keeping
  // the raw text locally lets the user type commas without each keystroke
  // round-tripping through parseCsv → formatCsv and dropping the comma.
  const [allergiesText, setAllergiesText] = useState(formatCsv(profile.allergies));
  const [excludedFoodsText, setExcludedFoodsText] = useState(formatCsv(profile.excluded_foods));

  // Fetch profile on mount
  useEffect(() => {
    axiosClient.get('/profile').then((r) => {
      if (!edited.current) {
        const next = { ...EMPTY_PROFILE, ...r.data };
        setProfile(next);
        setAllergiesText(formatCsv(next.allergies));
        setExcludedFoodsText(formatCsv(next.excluded_foods));
      }
      setLoaded(true);
    }).catch(() => {
      setLoadFailed(true);
    });
    axiosClient.get('/profile/ftp-history').then((r) => setFtpHistory(r.data)).catch(() => {});
  }, []);

  const saveProfile = useCallback(async () => {
    if (!profile || !loaded) return;
    setSaving(true);
    setError('');
    setSaved(false);

    try {
      const resp = await axiosClient.put('/profile', profile);
      const updated = { ...EMPTY_PROFILE, ...resp.data };
      setProfile(updated);
      setAllergiesText(formatCsv(updated.allergies));
      setExcludedFoodsText(formatCsv(updated.excluded_foods));
      if (user) {
        storeSetUser({ ...user, profile: updated });
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError(t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [profile, loaded, user, storeSetUser, t]);

  const handleLogout = () => {
    if (globalThis.confirm(t('profile.confirmSignOut'))) {
      void signOut().then(() => {
        clearUser();
        globalThis.location.reload();
      });
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  let saveButtonText: string;
  if (!loaded) {
    saveButtonText = t('profile.loading');
  } else if (saving) {
    saveButtonText = t('profile.saving');
  } else if (saved) {
    saveButtonText = t('profile.saved');
  } else {
    saveButtonText = t('profile.save');
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-(--color-border) bg-(--color-bg-secondary) px-4 py-3">
        <h1 className="text-lg font-bold text-(--color-text-primary) max-w-3xl mx-auto">
          {t('profile.title')}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col gap-6">
          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="w-16 h-16 rounded-full border-2 border-(--color-border)"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-(--color-accent)/20 border-2 border-(--color-accent) flex items-center justify-center text-xl font-bold text-(--color-accent)">
                {initials}
              </div>
            )}
            <div>
              <p className="text-lg font-semibold text-(--color-text-primary)">{user?.name}</p>
              <p className="text-sm text-(--color-text-secondary)">{user?.email}</p>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex flex-wrap gap-2">
            {PROFILE_TABS.map((tabId) => (
              <button
                key={tabId}
                onClick={() => setTab(tabId)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${tabClass(tab, tabId)}`}
              >
                {t(`profile.tabs.${tabId}`)}
              </button>
            ))}
          </div>

          {tab === 'account' && <AccountTab user={user} onLogout={handleLogout} />}

          {tab === 'cycling' && (
            <CyclingTab
              profile={profile}
              setProfile={editProfile}
              ftpHistory={ftpHistory}
              error={error || (loadFailed ? t('profile.loadFailed') : '')}
              saving={saving || !loaded}
              saveLabel={saveButtonText}
              onSave={saveProfile}
            />
          )}

          {tab === 'nutrition' && (
            <NutritionTab
              profile={profile}
              setProfile={editProfile}
              allergiesText={allergiesText}
              setAllergiesText={setAllergiesText}
              excludedFoodsText={excludedFoodsText}
              setExcludedFoodsText={setExcludedFoodsText}
              error={error || (loadFailed ? t('profile.loadFailed') : '')}
              saving={saving || !loaded}
              saveLabel={saveButtonText}
              onSave={saveProfile}
            />
          )}

          {/* Integrations tab */}
          {tab === 'integrations' && (
            <div className="flex flex-col gap-4">
              <DeviceSetup />
              {(garminEnabled || intervalsEnabled || stravaEnabled) && (
                <h3 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
                  {t('sources.connected')}
                </h3>
              )}
              {garminEnabled && <GarminConnect />}
              {intervalsEnabled && <IntervalsConnect />}
              {stravaEnabled && <StravaConnect />}
              <p className="text-xs text-(--color-text-secondary)">
                {t('profile.integrationsHint')}
              </p>
              <ImportRidesCard />
            </div>
          )}

          {/* Danger Zone tab */}
          {tab === 'danger' && (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                <h3 className="text-sm font-semibold text-red-400 mb-2">{t('profile.danger.signOutTitle')}</h3>
                <p className="text-xs text-(--color-text-secondary) mb-3">
                  {t('profile.danger.signOutBody')}
                </p>
                <button
                  onClick={handleLogout}
                  className="px-6 py-2.5 rounded-xl text-sm font-bold bg-red-500 text-white shadow-lg shadow-red-500/30 hover:brightness-110 active:scale-[0.98] transition-all"
                >
                  {t('profile.danger.signOut')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
